import { useMemo } from 'react';
import { useLocation } from 'wouter';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Navbar } from '@/components/layout/Navbar';
import { SearchBar } from '@/components/SearchBar';
import { useCMS } from '@/contexts/CMSContext';
import { SEO } from '@/components/SEO';
import { CardGridItem } from '@/components/CardGridItem';
import { IndexTOC } from '@/components/IndexTOC';
import { CARLORBIZ_PROSE } from '@/lib/carlorbiz-prose';
import type { TabContent } from '@/types/cms';

interface CardGridPageProps {
  folderSlug: string;
  title?: string;
  intro?: string;
  heroImage?: string;
  heroImageAlt?: string;
}

interface Group {
  category: string | null;
  tabs: TabContent[];
}

function gridColsFor(count: number): string {
  if (count <= 1) return 'grid grid-cols-1';
  if (count === 2) return 'grid grid-cols-1 sm:grid-cols-2';
  return 'grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3';
}

function groupByCategory(tabs: TabContent[]): Group[] {
  const ungrouped: TabContent[] = [];
  const buckets = new Map<string, TabContent[]>();
  const order: string[] = [];

  for (const t of tabs) {
    if (!t.category) {
      ungrouped.push(t);
      continue;
    }
    if (!buckets.has(t.category)) {
      buckets.set(t.category, []);
      order.push(t.category);
    }
    buckets.get(t.category)!.push(t);
  }

  const groups: Group[] = [];
  if (ungrouped.length > 0) groups.push({ category: null, tabs: ungrouped });
  for (const cat of order) groups.push({ category: cat, tabs: buckets.get(cat)! });
  return groups;
}

const SHOW_VERIFIED_STAMP: Record<string, boolean> = {
  'insights': true,
  'services': true,
  'about-me': false,
};

const SEO_TITLES: Record<string, string> = {
  'about-me': 'About — Carlorbiz',
  'services': 'Services — Strategic Consulting & AI Knowledge Systems',
  'insights': 'Insights — Carlorbiz',
};

const SEO_DESCRIPTIONS: Record<string, string> = {
  'about-me': '30 years transforming organisations. Now building AI knowledge systems that work long after the engagement ends.',
  'services': 'POD methodology, AI-powered knowledge platforms, and the Nera PWA template. Built for organisations whose expertise needs to outlast any single engagement.',
  'insights': 'Strategic thinking on AI, knowledge systems, organisational transformation, and what constraint teaches us about competitive advantage.',
};

export function CardGridPage({ folderSlug, title, intro, heroImage, heroImageAlt }: CardGridPageProps) {
  const { tabs, folders, isLoading } = useCMS();
  const [, setLocation] = useLocation();

  const folder = useMemo(() => folders.find(f => f.slug === folderSlug), [folders, folderSlug]);
  const folderTabs = useMemo(() => {
    if (folder) {
      const byFolder = tabs.filter(t => t.folder_id === folder.id && t.is_visible).sort((a, b) => a.order_index - b.order_index);
      if (byFolder.length > 0) return byFolder;
    }
    return tabs.filter(t => t.page_slug === folderSlug && t.is_visible).sort((a, b) => a.order_index - b.order_index);
  }, [tabs, folder, folderSlug]);

  const groups = useMemo(() => groupByCategory(folderTabs), [folderTabs]);
  const pageTitle = title || folder?.label || folderSlug;
  const showVerifiedStamp = SHOW_VERIFIED_STAMP[folderSlug];

  const faqSchema = folderSlug === 'services' && folderTabs.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: folderTabs.filter(t => t.summary).map(t => ({
      "@type": "Question",
      name: t.label,
      acceptedAnswer: { "@type": "Answer", text: t.summary },
    })),
  } : undefined;

  const personSchema = folderSlug === 'about-me' ? {
    "@context": "https://schema.org",
    "@type": "Person",
    name: "Carla Taylor",
    jobTitle: "Founder & Strategic Consultant",
    description: "Strategic consultant helping organisations find what makes them irreplaceable. 30 years leading transformation across healthcare, hospitality, and professional services. Builder of AI-powered knowledge platforms.",
    image: "https://carlorbiz.com.au/images/carla-headshot.jpg",
    url: "https://carlorbiz.com.au/about-me",
    sameAs: [
      "https://www.linkedin.com/in/carlajane/",
      "https://carlorbiz.com.au"
    ],
    worksFor: {
      "@type": "Organization",
      name: "Carlorbiz",
      url: "https://carlorbiz.com.au",
      logo: "https://carlorbiz.com.au/og-image.png",
    },
    knowsAbout: [
      "Strategic consulting",
      "AI-powered knowledge platforms",
      "Digital transformation",
      "DAIS methodology",
      "Knowledge management",
      "Transformation staging"
    ],
  } : undefined;

  const collectionSchema = folderSlug === 'insights' && folderTabs.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: SEO_TITLES['insights'],
    description: SEO_DESCRIPTIONS['insights'],
    url: "https://carlorbiz.com.au/insights",
    hasPart: folderTabs.filter(t => t.content && t.content.length > 200).map(t => ({
      "@type": "Article",
      headline: t.label,
      description: t.summary || t.label,
      url: `https://carlorbiz.com.au/insights/${t.slug}`,
      author: { "@type": "Person", name: "Carla Taylor" },
    })),
  } : undefined;

  const jsonLdSchemas: Record<string, unknown>[] = [];
  if (faqSchema) jsonLdSchemas.push(faqSchema);
  if (personSchema) jsonLdSchemas.push(personSchema);
  if (collectionSchema) jsonLdSchemas.push(collectionSchema);

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col bg-white">
        <Navbar />
        <div className="flex-1 flex items-center justify-center">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <SEO
        title={SEO_TITLES[folderSlug] || pageTitle}
        description={SEO_DESCRIPTIONS[folderSlug]}
        canonicalUrl={`https://carlorbiz.com.au/${folderSlug}`}
        jsonLd={jsonLdSchemas.length > 0 ? jsonLdSchemas : undefined}
      />
      <Navbar />

      <main className="flex-1 w-full bg-[#F9F9F9]">
        <section className="bg-white text-[var(--color-brand-dark)] py-16 lg:py-24 relative overflow-hidden border-b border-gray-100">
          <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-gradient-to-bl from-[var(--color-brand-primary)] to-transparent rounded-full opacity-5 blur-3xl pointer-events-none transform translate-x-1/2 -translate-y-1/2" />

          <div className="container mx-auto px-4 lg:px-8 relative z-10 flex flex-col lg:flex-row items-center gap-12">
            <div className="flex-1 space-y-6">
              <h1 className="font-heading text-4xl lg:text-5xl font-extrabold leading-tight drop-shadow-sm">
                <span className="text-[#2D7E32]">{pageTitle.split(' ')[0]} </span>
                {pageTitle.split(' ').length > 1 && (
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#6B7C73] to-[#AAB2B7]">
                    {pageTitle.split(' ').slice(1).join(' ')}
                  </span>
                )}
              </h1>
              {intro && (
                <div className={`max-w-2xl ${CARLORBIZ_PROSE}`}>
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{intro}</ReactMarkdown>
                </div>
              )}
            </div>

            {heroImage && (
              <div className="flex-1 flex justify-center w-full">
                <div className="relative w-full max-w-md flex items-center justify-center p-4">
                  <img
                    src={heroImage}
                    alt={heroImageAlt || pageTitle}
                    loading="eager"
                    className="w-full h-auto object-contain mix-blend-multiply hover:scale-[1.02] transition-transform duration-700 ease-out drop-shadow-lg"
                  />
                </div>
              </div>
            )}
          </div>
        </section>

        <div className="container mx-auto px-4 lg:px-8 py-12">
          {folderTabs.length === 0 ? (
            <p className="text-muted-foreground text-center py-16">
              Content coming soon.
            </p>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-8 lg:gap-12">
              <aside className="lg:sticky lg:top-6 lg:self-start">
                <IndexTOC folderSlug={folderSlug} tabs={folderTabs} />
              </aside>

              <div className="space-y-12">
                {groups.map((group, gi) => (
                  <section key={group.category ?? `ungrouped-${gi}`}>
                    {group.category && (
                      <div className="mb-6">
                        <h2 className="font-heading text-2xl lg:text-3xl font-extrabold">
                          <span className="text-[#2D7E32]">{group.category.split(' ')[0]} </span>
                          {group.category.split(' ').length > 1 && (
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#6B7C73] to-[#AAB2B7]">
                              {group.category.split(' ').slice(1).join(' ')}
                            </span>
                          )}
                        </h2>
                      </div>
                    )}
                    <div className={`${gridColsFor(group.tabs.length)} gap-5 lg:gap-6`}>
                      {group.tabs.map((tab) => (
                        <CardGridItem
                          key={tab.id}
                          folderSlug={folderSlug}
                          tab={tab}
                          showVerifiedStamp={showVerifiedStamp}
                          variant={group.tabs.length === 1 ? 'horizontal' : 'vertical'}
                        />
                      ))}
                    </div>
                  </section>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>

      <footer className="bg-white border-t border-gray-100 py-10">
        <div className="container mx-auto px-4">
          {folderTabs.length > 0 && (
            <div className="flex flex-col items-center mb-8">
              <p className="font-body text-xs text-gray-400 uppercase tracking-[0.15em] mb-3">
                Looking for something specific?
              </p>
              <div className="w-full max-w-md">
                <SearchBar
                  onResultSelect={(tabId) => {
                    const target = tabs.find(t => t.id === tabId);
                    if (target?.page_slug && target?.slug) {
                      setLocation(`/${target.page_slug}/${target.slug}`);
                    }
                  }}
                />
              </div>
            </div>
          )}

          <div className="text-center">
            <img
              src="/images/carlorbiz-logo.webp"
              alt="Carlorbiz Logo"
              className="h-16 mb-5 mx-auto w-auto object-contain"
            />
            <p className="text-xs text-gray-400 font-body uppercase tracking-wider">
              &copy; Carla Taylor t/as Carlorbiz, {new Date().getFullYear()}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
